import classNames from "classnames";
import { useFeature } from "flagged";
import moment from "moment/moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { DatePicker } from "rsuite";

import { CalendarIcon } from "components/icons/CalendarIcon";
import { DeleteIcon } from "components/icons/DeleteIcon";
import Warning from "components/icons/Warning";
import {
    BMP_WORKFLOW_LICENSE_ID,
    displayAmountErrorLabel,
    FEATURE_LICENSES,
    isExpirationDateValid,
    isSubscriptionLicense,
} from "components/licenses/common";
import style from "components/licenses/delivery-history/add-license-delivery.scss";
import {
    addMoreLicenses,
    createDefaultSelectedLicense,
    DeliveryForm,
    License,
} from "components/licenses/delivery-history/DeliveryFormContent";
import GlobalDateAmountComponent from "components/licenses/GlobalExpirationDateAndAmount";
import SubscriptionExpiryWarningBanner from "components/licenses/subscription-expiry-warning-banner/SubscriptionExpiryWarningBanner";
import StaticTable from "components/support/api-guide/StaticTable";
import Tooltip from "components/tooltip/Tooltip";
import { MIN_DATE } from "domain/licenses";
import { FLAG_BMP_WORKFLOWS } from "services/feature/FeatureFlagService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie, isUserParentInternal } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { DATE_FORMAT, formatFormDate, formatFormDateWithoutTime, generateLastDate, parseDate } from "utils/format";

import testIds from "testIds.json";

interface Props {
    availableLicenses: License[];
    ownedLicenses: License[];
    setSelectedLicenses: (licenses: License[]) => void;
    selectedLicenses: License[];
    newDeal: boolean;
    deliveryForm: DeliveryForm;
    parentDrawbackPrevention: boolean;
    setWarningFlag?: (value: boolean) => void;
    setLicenseSelectionErrors?: (value: boolean) => void;
}

const connector = connect((state: StoreState) => ({
    theme: state.themeReducer.theme,
}));

const LicenseSelectionForm = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const selectedLicensesImmutable = props.ownedLicenses.length > 0 ? props.ownedLicenses : [];
    const [selectedSubscriptionLicenses, setSelectedSubscriptionLicenses] = React.useState<string[]>([]);
    const DEFAULT_SELECTED_LICENSE = createDefaultSelectedLicense(t);
    const [globalSettersVisibility, setGlobalSettersVisibility] = React.useState(false);
    const areAllSelectedInAvailable = props.selectedLicenses.every((selected) =>
        props.availableLicenses.some((available) => available.licenseType === selected.licenseType)
    );
    const areAllAvailableInSelected = props.availableLicenses.every((available) =>
        props.selectedLicenses.some((selected) => available.licenseType === selected.licenseType)
    );

    const addRow = () => {
        usageStatisticsService.sendEvent({
            category: Category.LICENSE_DELIVERY,
            action: Action.ADD_LICENSE_TO_DELIVERY,
        });
        const newAdded: License = {
            licenseType: DEFAULT_SELECTED_LICENSE.licenseType,
            expirationDate: props.selectedLicenses[props.selectedLicenses.length - 1].expirationDate,
            productName: DEFAULT_SELECTED_LICENSE.productName,
            available: DEFAULT_SELECTED_LICENSE.available,
            licensesToAdd: DEFAULT_SELECTED_LICENSE.licensesToAdd,
            totalOfLicenses: DEFAULT_SELECTED_LICENSE.totalOfLicenses,
            index: Math.max(...props.selectedLicenses.map((item) => item.index)) + 1,
            parentAvailableAmount: DEFAULT_SELECTED_LICENSE.parentAvailableAmount,
            parentLicenseExpirationDate: DEFAULT_SELECTED_LICENSE.parentLicenseExpirationDate,
        };
        const licenses = props.selectedLicenses.concat([newAdded]);
        props.setSelectedLicenses(licenses);
    };

    const removeRow = (index: number) => {
        if (props.selectedLicenses.length === 1) {
            props.setSelectedLicenses([createDefaultSelectedLicense(t)]);
        } else {
            props.setSelectedLicenses(props.selectedLicenses.filter((each) => each.index !== index));
        }
    };
    if (!props.newDeal) {
        const rebuildSubscriptionLicenses = () => {
            if (!isUserParentInternal() || hasTenantCookie()) {
                const licenseListBuilt = props.selectedLicenses
                    .filter((e) => e.available == 1)
                    .filter((e) => FEATURE_LICENSES.includes(e.licenseType) || isSubscriptionLicense(e.licenseType))
                    .map((e) => e.productName);

                setSelectedSubscriptionLicenses(licenseListBuilt);
            }
        };

        React.useEffect(() => {
            rebuildSubscriptionLicenses();
        }, []);
    }

    React.useEffect(() => {
        const errorHandler = props.setLicenseSelectionErrors;
        if (errorHandler !== undefined) {
            let hasError = false;
            props.selectedLicenses.forEach((license) => {
                if (displayAmountErrorLabel(license, t) != null) {
                    hasError = true;
                    errorHandler(true);
                    return;
                }
            });
            if (!hasError) {
                errorHandler(false);
            }
        }
    }, [props.selectedLicenses]);

    React.useEffect(() => {
        if (props.ownedLicenses.length > 0) {
            const sortedLicenses = props.ownedLicenses.sort((value1, value2) =>
                value1.productName.localeCompare(value2.productName)
            );
            props.setSelectedLicenses(sortedLicenses);
        }
    }, []);

    const isAddMoreButtonHidden = (): boolean => {
        return areAllAvailableInSelected;
    };

    const updateWarningFlag = () => {
        if (props.setWarningFlag !== undefined) {
            props?.setWarningFlag(true);
        }
    };

    function getDropDownLicensesList() {
        const selectedLicensesChanged = selectedLicensesImmutable !== props.selectedLicenses;
        const areSomeSelectedInAvailable = props.selectedLicenses.some((selected) =>
            props.availableLicenses.some((available) => available.licenseType === selected.licenseType)
        );
        if (props.availableLicenses.length < 0) {
            return props.selectedLicenses;
        } else if (areSomeSelectedInAvailable && selectedLicensesChanged) {
            return getDistinctList([...props.selectedLicenses, ...props.availableLicenses]);
        } else if (areAllSelectedInAvailable || areSomeSelectedInAvailable) {
            return props.availableLicenses;
        } else {
            return getDistinctList([...props.selectedLicenses, ...props.availableLicenses]);
        }
    }

    function getDistinctList(licenses: License[]) {
        return Array.from(new Map(licenses.map((item) => [item.licenseType, item])).values());
    }

    function getSelectedDateValue(license: License) {
        const formatted = formatFormDateWithoutTime(license.expirationDate);
        return parseDate(formatted);
    }

    function showGlobalSetters(): JSX.Element {
        return (
            <button
                className={classNames(style.link, buttons.textButton)}
                onClick={() => setGlobalSettersVisibility(!globalSettersVisibility)}
                type="button"
                data-testid={
                    testIds.workArea.license.deliveryHistory.createDeliveryDialog.globalSettersVisibilityButton
                }
            >
                {globalSettersVisibility
                    ? t("DeliveryHistory.addLicenseDelivery.bulkActionsHidden")
                    : t("DeliveryHistory.addLicenseDelivery.bulkActions")}
            </button>
        );
    }

    const handleOkClick = (date: Date, license: License) => {
        props.setSelectedLicenses(
            props.selectedLicenses.map((each) => {
                if (each.index === license.index) {
                    each.expirationDate = formatFormDate(date.toString());
                }
                return each;
            })
        );
    };

    const bmpWorkflows = useFeature(FLAG_BMP_WORKFLOWS);
    const dropDownLicensesList: License[] = bmpWorkflows
        ? getDropDownLicensesList()
        : getDropDownLicensesList().filter((each) => each.licenseType !== BMP_WORKFLOW_LICENSE_ID);

    return (
        <>
            <div className={classNames(form.formFields)}>
                <SubscriptionExpiryWarningBanner productNames={selectedSubscriptionLicenses} />
            </div>
            <div hidden={!globalSettersVisibility}>
                <GlobalDateAmountComponent
                    showAmountType={false}
                    amount={props.deliveryForm.amount}
                    expirationDate={props.deliveryForm.expirationDate}
                    onAmountChange={(value) => {
                        props.deliveryForm["amount"] = parseInt(value);
                        props.setSelectedLicenses(
                            props.selectedLicenses.map((each) => ({
                                ...each,
                                licensesToAdd: parseInt(value),
                                totalOfLicenses: isNaN(parseInt(value))
                                    ? each.available
                                    : each.available + parseInt(value),
                            }))
                        );
                    }}
                    dateString={generateLastDate()}
                    onDateChange={(value) => {
                        props.deliveryForm["expirationDate"] = value;
                        props.setSelectedLicenses(
                            props.selectedLicenses.map((each) => ({
                                ...each,
                                expirationDate: value,
                            }))
                        );
                    }}
                />
            </div>
            <StaticTable
                headers={[
                    {
                        value: t("DeliveryHistory.addLicenseDelivery.table.licenseType"),
                    },
                    {
                        value: t("DeliveryHistory.addLicenseDelivery.table.available"),
                    },
                    {
                        value: t("DeliveryHistory.addLicenseDelivery.table.licensesToAdd"),
                    },
                    {
                        value: t("DeliveryHistory.addLicenseDelivery.table.totalOfLicenses"),
                    },
                    {
                        value: t("DeliveryHistory.addLicenseDelivery.table.remainingLicenses"),
                    },
                    {
                        value: t("DeliveryHistory.addLicenseDelivery.table.expirationDate"),
                    },
                ]}
                cells={props.selectedLicenses.map((license) => {
                    return [
                        <Tooltip key={license.licenseType} content={license.productName}>
                            <select
                                className={classNames(
                                    form.select,
                                    style.fixedWidthInput,
                                    style.ellipsis,
                                    style.selectArrow
                                )}
                                id={"licenseType" + license.index}
                                name={"licenseType" + license.index}
                                key={"licenseType" + license.index}
                                onChange={(e) => {
                                    const selected = getDropDownLicensesList().find(
                                        (item) => item.licenseType === e.target.value
                                    );
                                    if (selected !== undefined) {
                                        props.setSelectedLicenses(
                                            props.selectedLicenses.map((each) => {
                                                if (license.index === each.index) {
                                                    each = selected;
                                                    each.index = license.index;
                                                    const selectedLicensesLength = props.selectedLicenses.length;
                                                    if (
                                                        selectedLicensesLength >= 1 &&
                                                        each.available == 0 &&
                                                        license.licenseType === "default"
                                                    ) {
                                                        each.assigned = 0;
                                                    }
                                                    if (props.deliveryForm.amount != null) {
                                                        each.licensesToAdd = props.deliveryForm.amount;
                                                        each.totalOfLicenses = isNaN(props.deliveryForm.amount)
                                                            ? each.available
                                                            : each.available + props.deliveryForm.amount;
                                                        each.assigned = 0;
                                                    }
                                                    if (props.deliveryForm.expirationDate != "") {
                                                        each.expirationDate = props.deliveryForm.expirationDate;
                                                    }
                                                }
                                                return each;
                                            })
                                        );
                                    }
                                }}
                                onClick={() => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.LICENSE_DELIVERY,
                                        action: Action.CHANGE_LICENSE_TYPE,
                                    });
                                }}
                                value={license.licenseType}
                                data-testid={
                                    testIds.workArea.license.deliveryHistory.createDeliveryDialog.licenseTypeSelect
                                        .itself
                                }
                            >
                                <option value={"default"}>
                                    {t("DeliveryHistory.addLicenseDelivery.table.default")}
                                </option>
                                {dropDownLicensesList
                                    .filter((each) => each.licenseType != "default")
                                    .map((each) => (
                                        <option
                                            key={each.licenseType}
                                            value={each.licenseType}
                                            defaultValue={license.licenseType}
                                            hidden={
                                                each.licenseType !== license.licenseType &&
                                                props.selectedLicenses.some(
                                                    (item) => item.licenseType === each.licenseType
                                                )
                                            }
                                        >
                                            {each.productName}
                                        </option>
                                    ))}
                            </select>
                        </Tooltip>,
                        <div
                            key={"available" + license.index}
                            className={style.label}
                            data-testid={testIds.workArea.license.deliveryHistory.createDeliveryDialog.availableLabel}
                        >
                            {(!isUserParentInternal() || hasTenantCookie()) &&
                            (isSubscriptionLicense(license.licenseType) ||
                                FEATURE_LICENSES.includes(license.licenseType))
                                ? t("DeliveryHistory.addLicenseDelivery.table.subscription", {
                                      totalSubscriptionLicenses: license.available,
                                  })
                                : license.available}
                        </div>,
                        <div key={"assigned" + license.index}>
                            <input
                                autoFocus
                                className={classNames(form.input, style.fixedWidth)}
                                key={"added" + license.index}
                                id={"added" + license.index}
                                type={"number"}
                                onChange={(e) => {
                                    const inputValue = e.target.value;
                                    props.setSelectedLicenses(
                                        props.selectedLicenses.map((each) => {
                                            if (each.index === license.index) {
                                                each.licensesToAdd = inputValue === "" ? 0 : parseInt(inputValue) || 0;
                                                each.totalOfLicenses = isNaN(each.licensesToAdd)
                                                    ? each.available
                                                    : each.available + each.licensesToAdd;
                                            }
                                            return each;
                                        })
                                    );
                                }}
                                value={license.licensesToAdd.toString()}
                                data-testid={
                                    testIds.workArea.license.deliveryHistory.createDeliveryDialog.assignedAmountInput
                                        .itself
                                }
                            />
                            {license.licensesToAdd < 0 &&
                                props.parentDrawbackPrevention &&
                                license.available > 0 &&
                                (updateWarningFlag(),
                                (
                                    <Tooltip content={t("DeliveryHistory.addLicenseDelivery.negativeAmountWarning")}>
                                        <div className={style.warningIconWidth}>
                                            <Warning
                                                color={props.theme.secondaryWarningBackgroundColor}
                                                width={20}
                                                height={20}
                                            />
                                        </div>
                                    </Tooltip>
                                ))}
                            {displayAmountErrorLabel(license, t)}
                        </div>,
                        <div
                            key={"total" + license.index}
                            className={style.totalLabel}
                            data-testid={
                                testIds.workArea.license.deliveryHistory.createDeliveryDialog.totalOfLicensesLabel
                                    .itself
                            }
                        >
                            {license.totalOfLicenses}
                            {license.totalOfLicenses < 0 ? (
                                <div
                                    className={form.error}
                                    data-testid={
                                        testIds.workArea.license.deliveryHistory.createDeliveryDialog
                                            .totalOfLicensesLabel.errorLabel
                                    }
                                >
                                    <span>{t("DeliveryHistory.addLicenseDelivery.validation.totalOfLicenses")}</span>
                                </div>
                            ) : (
                                ""
                            )}
                        </div>,
                        <div
                            key={"remaining" + license.index}
                            className={style.label}
                            data-testid={testIds.workArea.license.deliveryHistory.createDeliveryDialog.availableLabel}
                        >
                            {!(license.licensesToAdd < 0 && props.parentDrawbackPrevention && license.available > 0) &&
                            license.licensesToAdd
                                ? license.parentAvailableAmount - license.licensesToAdd
                                : license.parentAvailableAmount}
                        </div>,
                        <div key={"expirationDate" + license.index} className={style.gridColumns}>
                            <DatePicker
                                format={DATE_FORMAT}
                                id={"expirationInput" + license.index}
                                caretAs={(colorProps) => (
                                    <CalendarIcon {...colorProps} color={props.theme.activeBackgroundColor} />
                                )}
                                className={classNames(form.dateRange, {
                                    [form.inputError]: !isExpirationDateValid(
                                        license.expirationDate,
                                        formatFormDate(
                                            license.parentLicenseExpirationDate
                                                ? license.parentLicenseExpirationDate
                                                : generateLastDate()
                                        )
                                    ),
                                })}
                                placement={"autoVerticalEnd"}
                                onSelect={(date: Date) => {
                                    handleOkClick(date, license);
                                }}
                                cleanable={false}
                                disabledDate={(date: Date) => {
                                    const normalizedDate = new Date(
                                        date.getFullYear(),
                                        date.getMonth(),
                                        date.getDate()
                                    );
                                    const lastDate = formatFormDate(
                                        license.parentLicenseExpirationDate
                                            ? license.parentLicenseExpirationDate
                                            : generateLastDate()
                                    );
                                    return normalizedDate > moment(lastDate).toDate();
                                }}
                                value={getSelectedDateValue(license)}
                                data-testid={
                                    testIds.workArea.license.deliveryHistory.createDeliveryDialog.expirationDateInput
                                        .itself
                                }
                                editable={true}
                                renderValue={(value: Date) => {
                                    return moment(value).format("DD/MM/YYYY");
                                }}
                            />
                            {isExpirationDateValid(
                                license.expirationDate,
                                formatFormDate(
                                    license.parentLicenseExpirationDate
                                        ? license.parentLicenseExpirationDate
                                        : generateLastDate()
                                )
                            ) ? null : (
                                <div
                                    className={form.error}
                                    data-testid={
                                        testIds.workArea.license.deliveryHistory.createDeliveryDialog
                                            .expirationDateInput.errorLabel
                                    }
                                >
                                    <span className={style.wrapped}>
                                        {t("DeliveryHistory.addLicenseDelivery.validation.expirationDate", {
                                            minDate: MIN_DATE.toLocaleDateString(),
                                            maxDate: formatFormDateWithoutTime(
                                                license.parentLicenseExpirationDate
                                                    ? license.parentLicenseExpirationDate
                                                    : generateLastDate()
                                            ),
                                        })}
                                    </span>
                                </div>
                            )}
                            <div key={"deleteIcon" + license.index} className={style.gridRows}>
                                {props.selectedLicenses.length >= 1 ? (
                                    <div
                                        key={"deleteRow" + license.index}
                                        className={classNames(style.margin, buttons.textButton)}
                                        onClick={() => {
                                            usageStatisticsService.sendEvent({
                                                category: Category.LICENSE_DELIVERY,
                                                action: Action.REMOVE_LICENSE,
                                                label: license.licenseType,
                                            });
                                            removeRow(license.index);
                                        }}
                                        data-testid={
                                            testIds.workArea.license.deliveryHistory.createDeliveryDialog
                                                .removeRowButton
                                        }
                                    >
                                        <DeleteIcon
                                            color={props.theme.contentBackgroundColor}
                                            linecolor={props.theme.iconFillColor}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>,
                    ];
                })}
            />
            <button
                onClick={addRow}
                className={classNames(style.link, buttons.textButton, style.addMoreButton)}
                data-testid={testIds.workArea.license.deliveryHistory.createDeliveryDialog.addLicenseButton}
                hidden={isAddMoreButtonHidden()}
                type="button"
            >
                {addMoreLicenses(
                    props.theme.contentBackgroundColor,
                    props.theme.iconFillColor,
                    t("Common.addMoreLicenses")
                )}
            </button>
            {showGlobalSetters()}
        </>
    );
};

export default connector(LicenseSelectionForm);
